// Relative (not '@/') so plain `node --test` can load this module too.
import { EXAMPLE_EMPLOYEE_COUNT } from './demoLimits.js'
import { TEAM } from '../content/team.js'

export const SITE_URL = 'https://www.retira.app'
export const SITE_NAME = 'retira.app'
export const DEFAULT_SOCIAL_IMAGE = `${SITE_URL}/logo.png`

export const HOME_FAQS = [
  {
    id: 'purpose',
    question: 'What does the retira.app 401(k) plan modeling calculator do?',
    answer:
      'The calculator models employee and employer contributions for a 401(k) plan using census data and plan rules you provide. Profit sharing includes one guided six-formula designer with allocation groups and live modeled cross-testing. When cash balance is enabled, that same designer runs scenario-grade combined DB/DC testing and a disclosed §415(b) owner-credit solve. Actuarial funding still requires an enrolled actuary.',
  },
  {
    id: 'cash-balance',
    question: 'Does retira.app provide cash-balance plan funding or an actuarial valuation?',
    answer:
      'No. retira.app does not provide required funding or an actuarial valuation. It can model explicit adopted credits and, in the combined Maximize owners designer, a scenario-grade §415(b)-limited owner credit using disclosed proposal-derived actuarial assumptions. An enrolled actuary must confirm the assumptions and supply the funding corridor.',
  },
  {
    id: 'compare',
    question: 'Can I compare two 401(k) plan designs?',
    answer:
      'Yes. Plan A is the baseline and you can add up to three alternative designs (Plan B–D) compared side by side – plan metrics, compliance screens, and employee-level results all show the differences. You can change matching contributions, nonelective contributions, profit sharing, eligibility, vesting, termination rules, and after-tax contribution settings to see the estimated effect.',
  },
  {
    id: 'owner-heavy',
    question: 'Can it model owner-heavy designs?',
    answer:
      'Yes. The guided profit-sharing designer supports new-comparability allocation groups and a Maximize owners solve. Owner-heavy designs show the modeled minimum-allocation gateway, equivalent-benefit rate groups, and ABPT live under documented assumptions; the plan’s TPA or actuary must confirm the formal result.',
  },
  {
    id: 'data',
    question: 'Where is employee census data stored?',
    answer:
      'Employee census data is stored in local browser storage on your device and is never uploaded to retira.app. Calculations run in your browser. Clear the census or this site’s browser storage to remove it, and use a private window or clear site data when working on a shared computer.',
    link: { label: 'Read the full privacy and terms.', to: '/privacy' },
  },
  {
    id: 'irs',
    question: 'Which IRS 401(k) limits does the calculator use?',
    answer:
      'The calculator supports selectable plan years 2025 and 2026 and retains 2023 and 2024 limits for required lookback calculations. It applies the selected year’s elective-deferral, catch-up, annual-additions, compensation-cap, and HCE-threshold values automatically.',
  },
  {
    id: 'compliance',
    question: 'Does retira.app perform official 401(k) compliance testing?',
    answer:
      'No. The calculator provides explicitly informal ADP-style, ACP-style, employer-benefit coverage-style, cross-testing, and top-heavy screens from the loaded census. They are planning indicators, not formal nondiscrimination tests or filing-ready compliance determinations.',
  },
  {
    id: 'advice',
    question: 'Can I use the results as legal, tax, or plan advice?',
    answer:
      'No. Outputs depend on the information and assumptions you provide. Verify decisions against the governing plan document and applicable rules, and have a qualified TPA, ERISA professional, tax advisor, or legal advisor review anything used for administration or filing.',
    link: { label: 'Contact us about a modeling case retira.app does not cover.', to: '/contact' },
  },
  {
    id: 'account',
    question: 'Do I need an account?',
    answer:
      `The demo runs on ${EXAMPLE_EMPLOYEE_COUNT} sample employees or your own census, with no sign-up. A signed-in Studio account adds Estimate Mode, Excel and multi-file census import, the Client Book, and Julio, the AI assistant, on your own saved plans.`,
  },
]

// Search treats "401k" and "401(k)" as distinct strings often enough that both
// spellings must appear across titles, descriptions, and structured data.
const HOME_DESCRIPTION =
  '401k plan design calculator for advisors, TPAs, and employers. Model 401(k) and cash-balance retirement plans: cost, participation, vesting, compliance screens.'

export const SEO_BY_PATH = {
  '/': {
    title: '401k Plan Design Calculator & 401(k) Modeling | retira.app',
    description: HOME_DESCRIPTION,
    canonical: `${SITE_URL}/`,
  },
  '/demo': {
    title: '401k Plan Design Calculator Demo | retira.app',
    description:
      'Try the 401k calculator demo with 100 sample employees. Compare 401(k) plan designs side by side for cost, participation, contributions, and compliance signals.',
    canonical: `${SITE_URL}/demo`,
  },
  '/cfo': {
    title: '401k Cost Calculator for CFOs | retira.app',
    description:
      'See what your 401k really costs: employer cost, worst-case exposure, after-tax net cost, and a five-year projection. Model your 401(k) in a minute or upload a census.',
    canonical: `${SITE_URL}/cfo`,
    // Unlisted route: reachable by direct link, kept out of search.
    robots: 'noindex, nofollow',
  },
  '/contact': {
    title: 'Contact retira.app | 401k Plan Modeling Support',
    description:
      'Contact the retira.app team about 401k plan modeling questions, product support, feature requests, or 401(k) plan-design scenarios the calculator does not yet cover.',
    canonical: `${SITE_URL}/contact`,
  },
  '/book': {
    title: 'Book a Call | retira.app 401k Plan Modeling',
    description:
      'Book a 30-minute call with the retira.app founders to walk through 401k plan modeling against your plan. Pick a time that works when the team is free.',
    canonical: `${SITE_URL}/book`,
  },
  '/privacy': {
    title: 'Privacy & Terms | retira.app 401(k) Plan Modeling',
    description:
      'Learn how retira.app keeps employee census data in your browser, handles account and saved plan-setting data, and defines the terms for its 401k modeling estimates.',
    canonical: `${SITE_URL}/privacy`,
  },
}

export function normalizeSeoPath(pathname = '/') {
  const path = String(pathname || '/').split(/[?#]/, 1)[0]
  if (path === '/') return '/'
  return path.replace(/\/+$/, '') || '/'
}

export function getSeoForPath(pathname = '/') {
  return SEO_BY_PATH[normalizeSeoPath(pathname)] || SEO_BY_PATH['/']
}

// Person entities for the team (rendered visibly in the landing About section)
// so searches for the people behind Retira – especially "Isaiah Rice" – connect
// them to the product and to retirement-plan topics.
const RETIREMENT_TOPICS = [
  '401k plan design',
  '401(k) retirement plans',
  'cash balance plans',
  'retirement plan compliance screening',
]

function slugifyName(name) {
  return String(name).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

function teamPersonEntities() {
  return TEAM.filter(({ anonymousGroup }) => !anonymousGroup).map(({ name, role, photo, founder }) => ({
    '@type': 'Person',
    '@id': `${SITE_URL}/#person-${slugifyName(name)}`,
    name,
    jobTitle: founder ? `Co-founder · ${role}` : role,
    worksFor: { '@id': `${SITE_URL}/#organization` },
    knowsAbout: RETIREMENT_TOPICS,
    url: `${SITE_URL}/#about`,
    ...(photo ? { image: `${SITE_URL}${photo}` } : {}),
    ...(founder ? { description: `Co-founder of Retira (Retira LLC), the 401k plan design calculator at retira.app.` } : {}),
  }))
}

function breadcrumbSchema(name, path) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name, item: `${SITE_URL}${path}` },
    ],
  }
}

export function getStructuredData(pathname = '/') {
  const path = normalizeSeoPath(pathname)
  const seo = getSeoForPath(path)
  const people = teamPersonEntities()
  const organization = {
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: SITE_NAME,
    alternateName: ['Retira', 'Retira 401k Calculator'],
    legalName: 'Retira LLC',
    url: `${SITE_URL}/`,
    logo: { '@type': 'ImageObject', url: DEFAULT_SOCIAL_IMAGE },
    email: 'hello@retira.app',
    knowsAbout: RETIREMENT_TOPICS,
    founder: TEAM.filter((member) => member.founder).map((member) => ({
      '@id': `${SITE_URL}/#person-${slugifyName(member.name)}`,
    })),
    member: people.map((person) => ({ '@id': person['@id'] })),
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: 'support@retira.app',
        availableLanguage: 'English',
      },
    ],
  }
  const website = {
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: SITE_NAME,
    alternateName: ['Retira', '401k plan design calculator', '401k calculator'],
    url: `${SITE_URL}/`,
    description: HOME_DESCRIPTION,
    keywords: '401k, 401(k), 401k calculator, retirement plan design, cash balance plan',
    publisher: { '@id': `${SITE_URL}/#organization` },
    inLanguage: 'en-US',
  }

  if (path === '/') {
    return {
      '@context': 'https://schema.org',
      '@graph': [
        organization,
        website,
        ...people,
        {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/#webpage`,
          url: `${SITE_URL}/`,
          name: seo.title,
          description: seo.description,
          isPartOf: { '@id': `${SITE_URL}/#website` },
          about: { '@id': `${SITE_URL}/#software` },
          inLanguage: 'en-US',
        },
        {
          '@type': 'SoftwareApplication',
          '@id': `${SITE_URL}/#software`,
          name: SITE_NAME,
          alternateName: ['Retira 401k Calculator', '401k plan design calculator'],
          url: `${SITE_URL}/`,
          applicationCategory: 'BusinessApplication',
          applicationSubCategory: '401k / 401(k) and cash-balance retirement plan design modeling',
          operatingSystem: 'Any modern web browser',
          description: seo.description,
          keywords:
            '401k, 401(k), 401k calculator, 401k plan design, retirement plan modeling, retirement plan comparison, cash balance plan, safe harbor 401k, profit sharing, new comparability',
          audience: {
            '@type': 'Audience',
            audienceType: 'Retirement plan advisors, consultants, TPAs, and employers',
          },
          featureList: [
            'Compare up to four 401(k) plan designs (Plan A-D) side by side',
            'Model employer matching, nonelective, profit sharing, new comparability, and after-tax contributions',
            'Illustrate explicit year-one cash-balance hypothetical benefit credits separately from DC cost and funding',
            'Estimate employee participation and contribution totals',
            'Model eligibility, vesting, terminations, and forfeitures',
            'Review informal ADP/ACP, coverage, cross-testing, and top-heavy compliance screens',
            'Ask the built-in AI assistant to analyze and optimize a plan design',
            'Export employee selections and plan summaries',
            'Keep employee census data in the browser',
          ],
          publisher: { '@id': `${SITE_URL}/#organization` },
        },
        {
          '@type': 'FAQPage',
          '@id': `${SITE_URL}/#faq`,
          mainEntity: HOME_FAQS.map(({ question, answer }) => ({
            '@type': 'Question',
            name: question,
            acceptedAnswer: { '@type': 'Answer', text: answer },
          })),
        },
      ],
    }
  }

  const pageType = path === '/contact' ? 'ContactPage' : 'WebPage'
  const pageName = path === '/demo'
    ? '401k Plan Design Calculator Demo'
    : path === '/cfo'
      ? '401k Cost Calculator for CFOs'
      : path === '/contact'
        ? 'Contact retira.app'
        : path === '/book'
          ? 'Book a Call'
          : 'Privacy & Terms'

  const graph = [
    organization,
    website,
    ...people,
    {
      '@type': pageType,
      '@id': `${seo.canonical}#webpage`,
      url: seo.canonical,
      name: seo.title,
      description: seo.description,
      isPartOf: { '@id': `${SITE_URL}/#website` },
      inLanguage: 'en-US',
      breadcrumb: { '@id': `${seo.canonical}#breadcrumb` },
    },
    { '@id': `${seo.canonical}#breadcrumb`, ...breadcrumbSchema(pageName, path) },
  ]

  if (path === '/demo') {
    graph.push({
      '@type': 'SoftwareApplication',
      '@id': `${SITE_URL}/demo#software`,
      name: 'retira.app 401(k) Plan Design Calculator Demo',
      alternateName: '401k calculator demo',
      url: `${SITE_URL}/demo`,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Any modern web browser',
      description: seo.description,
      isAccessibleForFree: true,
      publisher: { '@id': `${SITE_URL}/#organization` },
    })
  }

  return { '@context': 'https://schema.org', '@graph': graph }
}
